import * as React from "react";
import { ItemKeyHelper } from "src/components/utility/ItemKeyHelper";
import {
  UitkExperimentalButtonTabs,
  UitkTab,
  UitkTabsType,
  UitkTabAnchorType,
} from "uitk-react-experimental-button-tabs";
import { ClientWizardStateStore } from "src/stores/wizard/ClientWizardStateStore";
import { MobXProviderContext, observer } from "mobx-react";
import { QueryObject, RouteStore } from "bernie-core";
import { Navigation } from "src/stores/wizard/config";
import { FlexComponentNameToWizardStateConfig } from "../FlexComponentNameToWizardStateConfig";
import { useLocalization } from "@shared-ui/localization-context";
import { TabbedWizard } from "./TabbedWizard";

/**
 * @property lobForms: a list of React elements representing each LOB form added in the flex manager
 */
export interface LobFormWithTabsProps {
  lobForms: React.ReactElement[];
  wizardFormClassNames?: string;
  tabGroupClassNames?: string;
}

export const LobFormWithTabs: React.FunctionComponent<LobFormWithTabsProps> = observer(
  ({ lobForms, wizardFormClassNames, tabGroupClassNames }) => {
    const keyHelperWizard = new ItemKeyHelper("storefrontForms");
    const keyHelperNavigation = new ItemKeyHelper("storefrontWizardNavigation");
    const wizardState: ClientWizardStateStore = React.useContext(MobXProviderContext).wizardState;
    const routeStore: RouteStore = React.useContext(MobXProviderContext).routeStore;
    const defaultLobNavConfig: Navigation = {
      tabIcon: "help",
      tabTitleToken: "",
    };
    const { formatText } = useLocalization();

    //used by onTabSelect to update the query route
    const lobQueryCombiner = (prevQuery: QueryObject, nextQuery: QueryObject): QueryObject => {
      delete prevQuery.pwaSubLOB;
      delete prevQuery.packageSub;

      return { ...prevQuery, ...nextQuery };
    };

    //returns the localized string given the name of the flex component used
    const getLabel = (lobName: string): string => {
      let label = defaultLobNavConfig.tabTitleToken;
      if (wizardState[FlexComponentNameToWizardStateConfig[lobName]]) {
        label = formatText(wizardState[FlexComponentNameToWizardStateConfig[lobName]].config.navigation.tabTitleToken);
      }

      return label;
    };

    //set the state to hold the default lob in the wizard store as the current value
    const [currentLOB, setCurrentLOB] = React.useState<string>(getLabel(wizardState.activeLob));

    /**
     * Check the route store, if there is no LOB query attached, get it from the wizardState default and redirect
     * CurrentLOB and defaultLOB set server side, but may be updated based on last search cookie value client side
     * To ensure up to date values on client side render, trigger an effect that does not have any dependent values to only run on first render
     */
    React.useEffect(() => {
      if (!routeStore.queryParams.pwaLob) {
        const { useDefaultLOB, defaultLob: pwaLob } = wizardState;

        if (useDefaultLOB) {
          routeStore.updateSearch({ pwaLob });
          wizardState.setUseDefaultLOB(false);
          setCurrentLOB(getLabel(pwaLob));
        } else {
          if (typeof window !== "undefined" && window.history) {
            window.history.back();
          }
        }
      }
    }, []);

    //need to be able to map the localized version of the lob form name to the flex component name
    const LobFormNames: { [label: string]: string } = {};
    lobForms.map((lob: React.ReactElement) => {
      const formName = lob.props.flexComponent.name;
      const formLabel = getLabel(formName);
      LobFormNames[formLabel] = formName;
    });

    const getFormNameFromLabel = (label: string): string => {
      return LobFormNames[label];
    };

    //on selecting a tab, update the wizard state with the flex component name of the current tab, and update the react state with the localized label that will be used on the react component
    //update the query route with the new pwaLob and pwaSubLob
    const onTabSelect = (tabSelected: any) => {
      const formName = getFormNameFromLabel(tabSelected);
      wizardState.setActiveLob(formName);
      setCurrentLOB(tabSelected);

      let queryParams: any = { pwaLob: formName };
      const { subLOBState } = wizardState.lobStateMap[formName];

      resetErrorsState();

      if (subLOBState) {
        queryParams = { pwaLob: formName, pwaSubLOB: subLOBState };
      }
      routeStore.updateSearch(queryParams, {}, false, lobQueryCombiner);
    };

    const resetErrorsState = () => {
      wizardState.carWizardState.resetSubLobState();
      wizardState.carWizardState.resetSubLobTabs();
      wizardState.carWizardState.cleanErrorState();

      wizardState.lxWizardState.cleanErrorState();
      wizardState.hotelWizardState.cleanErrorState();
      wizardState.flightWizardState.cleanErrorState();
      wizardState.packageWizardState.cleanErrorState();
    };

    const lobIconsMap = {
      "wizard-hotel-pwa-v2": "lob_hotels",
      "wizard-flight-pwa": "lob_flights",
      "wizard-package-pwa": "lob_packages",
      "wizard-car-pwa": "lob_cars",
      "wizard-lx-pwa": "lob_activities",
      "wizard-cruise-pwa": "lob_cruise",
    };

    return (
      <>
        <UitkExperimentalButtonTabs
          onTabSelect={onTabSelect}
          selectedTab={currentLOB}
          tabsType={UitkTabsType.NATURAL}
          className={`lobNavigationFormWithTabs ${tabGroupClassNames ? tabGroupClassNames : ""}`}
          data-testid="lobNavigationFormWithTabs"
          tabAnchorType={UitkTabAnchorType.ICON_AND_TEXT}
        >
          {lobForms.map((lob: React.ReactElement) => (
            <UitkTab
              name={getLabel(lob.props.flexComponent.name)}
              targetURI={`?pwaLOB=${lob.props.flexComponent.name}`}
              id={lob.props.flexComponent.name}
              key={keyHelperNavigation.next()}
              iconKey={lobIconsMap[lob.props.flexComponent.name]}
            >
              {currentLOB === getLabel(lob.props.flexComponent.name) && (
                <div className={wizardFormClassNames || ""}>
                  <TabbedWizard key={keyHelperWizard.next()}>{lob}</TabbedWizard>
                </div>
              )}
            </UitkTab>
          ))}
        </UitkExperimentalButtonTabs>
      </>
    );
  }
);
